import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Play, Square, Terminal, Globe, Server, FolderInput, ArrowRight,
  CheckCircle2, XCircle, AlertTriangle, RefreshCw, ChevronUp, ChevronDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getAllServers, enqueueWebFilesMigration, streamWebFilesJob } from '@/services/api'

const LINE_CLR = {
  info: 'text-sky-700', cmd: 'text-amber-700', out: 'text-emerald-700',
  err: 'text-red-600', system: 'text-violet-700',
}

export default function WebFiles() {
  const [servers, setServers] = useState([])
  const [sourceServerId, setSourceServerId] = useState('')
  const [destServerId, setDestServerId] = useState('')
  const [sourcePath, setSourcePath] = useState('')
  const [destPath, setDestPath] = useState('')
  const [chownUser, setChownUser] = useState('')
  const [group, setGroup] = useState('psacln')
  const [webPerms, setWebPerms] = useState(true)
  const [mirror, setMirror] = useState(false)

  const [running, setRunning] = useState(false)
  const [status, setStatus] = useState(null) // 'success' | 'failed' | null
  const [lines, setLines] = useState([])
  const [showTerminal, setShowTerminal] = useState(true)
  const [error, setError] = useState(null)

  const streamRef = useRef(null)
  const endRef = useRef(null)

  useEffect(() => {
    getAllServers().then(setServers).catch(err => console.error('load servers', err))
    return () => streamRef.current?.close?.()
  }, [])

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [lines])

  const cpanelServers = servers.filter(s => s.type === 'CPANEL')
  const pleskServers  = servers.filter(s => s.type === 'PLESK')
  const srcServer = servers.find(s => s.id === sourceServerId)
  // Suggested paths reflect the selected source server's cPanel username.
  const srcUser = srcServer?.username || 'user'

  const addLine = (type, content) => setLines(prev => [...prev, {
    type, content: String(content ?? ''), id: `${Date.now()}-${Math.random()}`,
  }])

  const canStart = sourceServerId && destServerId && sourcePath.trim() && destPath.trim() && !running

  const handleMessage = ({ type, data }) => {
    switch (type) {
      case 'init':    addLine('info', data); break
      case 'output':  addLine('out', data.line); break
      case 'stderr':  addLine('err', data.line); break
      case 'complete':
        setStatus(data.success ? 'success' : 'failed')
        addLine(data.success ? 'info' : 'err',
          (data.success ? '✓ ' : '✗ ') + (data.message || (data.success ? 'Completed' : 'Failed')))
        setRunning(false)
        break
      case 'fatal':
        setStatus('failed'); setError(data); addLine('err', data); setRunning(false)
        break
      default: break
    }
  }

  const start = async () => {
    if (!canStart) return
    setRunning(true); setStatus(null); setError(null); setLines([])
    addLine('system', 'Queuing web-files migration…')
    try {
      // Credentials + host/IP come from the selected server's saved settings.
      const { jobId } = await enqueueWebFilesMigration({
        sourceServerId, destServerId,
        sourcePath: sourcePath.trim(), destPath: destPath.trim(),
        chownUser: chownUser.trim(), group: group.trim(), webPerms, del: mirror,
      })
      addLine('system', `Job queued (${jobId}). It runs on the server and survives page close / redeploys.`)
      streamRef.current = streamWebFilesJob(jobId, {
        onMessage: handleMessage,
        onError: (err) => { addLine('err', 'Stream lost: ' + (err?.message || '') + ' — the job keeps running.') },
        onComplete: () => setRunning(false),
      })
    } catch (e) {
      setError(e.message); addLine('err', 'Failed to queue: ' + e.message); setRunning(false)
    }
  }

  const cancel = () => {
    streamRef.current?.close?.()
    addLine('system', 'Cancelled')
    setRunning(false)
  }

  const selectCls = 'h-10 w-full rounded-md border border-black/10 bg-white/70 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary/40'

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Globe className="w-6 h-6 text-primary/70" /> Web Files Migration
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Copy a domain's web files from a source cPanel docroot to the destination Plesk vhost via rsync.
        </p>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">Transfer</CardTitle>
          <CardDescription>Runs rsync on the destination server, pulling from the source over SSH.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Servers */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Server className="w-3.5 h-3.5" /> Source (cPanel)</Label>
              <select className={selectCls} value={sourceServerId}
                onChange={e => setSourceServerId(e.target.value)}>
                <option value="">Select source server…</option>
                {cpanelServers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.host})</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Server className="w-3.5 h-3.5" /> Destination (Plesk)</Label>
              <select className={selectCls} value={destServerId}
                onChange={e => setDestServerId(e.target.value)}>
                <option value="">Select destination server…</option>
                {pleskServers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.host})</option>)}
              </select>
            </div>
          </div>

          {/* Paths */}
          <div className="space-y-2">
            <Label>Source path (on cPanel)</Label>
            <Input value={sourcePath} onChange={e => setSourcePath(e.target.value)}
              placeholder={`/home/${srcUser}/public_html   or   /home/${srcUser}/sub.domain.com`} />
          </div>
          <div className="space-y-2">
            <Label>Destination path (on Plesk)</Label>
            <Input value={destPath} onChange={e => setDestPath(e.target.value)}
              placeholder="/var/www/vhosts/<domain>/httpdocs" />
          </div>

          {/* Login + host/IP come from the selected server's settings. */}
          <div className="flex items-start gap-2 p-3 bg-accent border-0 rounded-lg">
            <Server className="w-4 h-4 text-accent-foreground/70 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-accent-foreground/80">
              The SSH user, host/IP and password come from the selected server's saved settings.
              Set the source server's <strong>host to its IP address</strong> in
              <strong> Servers</strong> so the transfer targets the old box even after DNS cut-over.
            </p>
          </div>

          {/* Optional Plesk ownership */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Plesk system user for chown <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input value={chownUser} onChange={e => setChownUser(e.target.value)}
                placeholder="e.g. acme.com_o9zay4rh6a" />
            </div>
            {chownUser.trim() && (
              <div className="space-y-2">
                <Label>Group</Label>
                <Input value={group} onChange={e => setGroup(e.target.value)} placeholder="psacln" />
              </div>
            )}
          </div>

          {/* Toggles */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="space-y-0.5">
              <Label className="font-medium">Apply standard web permissions</Label>
              <p className="text-xs text-muted-foreground">Directories 755, files 644 (--chmod)</p>
            </div>
            <Switch checked={webPerms} onCheckedChange={setWebPerms} />
          </div>
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="space-y-0.5">
              <Label className="font-medium text-destructive">Mirror (delete extraneous)</Label>
              <p className="text-xs text-muted-foreground">
                Removes destination files not present on the source (--delete). Destructive.
              </p>
            </div>
            <Switch checked={mirror} onCheckedChange={setMirror} />
          </div>

          {chownUser.trim() === '' && (
            <Alert className="border-0 bg-amber-50 text-amber-800 rounded-lg py-2">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription className="text-xs">
                No Plesk system user set — files won't be re-owned. On Plesk, set the subscription's
                system user (e.g. <code>domain.com_xxxx</code>) so the site serves correctly.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive" className="border-0 bg-destructive/8 text-destructive rounded-lg py-2">
              <AlertDescription className="text-xs">{error}</AlertDescription>
            </Alert>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {!running ? (
              <Button onClick={start} disabled={!canStart} className="gap-1.5">
                <Play className="w-4 h-4" /> Migrate Files
              </Button>
            ) : (
              <Button variant="destructive" onClick={cancel} className="gap-1.5">
                <Square className="w-4 h-4" /> Cancel
              </Button>
            )}
            {status === 'success' && (
              <span className="inline-flex items-center gap-1 text-xs font-medium chip-success px-2.5 py-1 rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5" /> Completed
              </span>
            )}
            {status === 'failed' && (
              <span className="inline-flex items-center gap-1 text-xs font-medium bg-destructive/10 text-destructive px-2.5 py-1 rounded-full">
                <XCircle className="w-3.5 h-3.5" /> Failed
              </span>
            )}
            {running && (
              <span className="inline-flex items-center gap-1.5 text-xs font-medium chip-amber px-2.5 py-1 rounded-full">
                <RefreshCw className="w-3 h-3 animate-spin" /> Running
              </span>
            )}
            <Button variant="ghost" className="ml-auto text-muted-foreground"
              onClick={() => setShowTerminal(v => !v)}>
              <Terminal className="w-4 h-4 mr-1.5" /> {showTerminal ? 'Hide' : 'Show'} Log
              {showTerminal ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
            </Button>
          </div>

          {/* Path preview */}
          {(sourcePath || destPath) && (
            <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground bg-black/[0.03] rounded-lg px-3 py-2 overflow-x-auto">
              <FolderInput className="w-3.5 h-3.5 shrink-0" />
              <span className="whitespace-nowrap">
                {(srcServer?.username || 'user')}@{srcServer?.host || 'source'}:{sourcePath || '…'}
              </span>
              <ArrowRight className="w-3.5 h-3.5 shrink-0" />
              <span className="whitespace-nowrap">{destPath || '…'}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Terminal */}
      {showTerminal && (
        <div className="rounded-2xl overflow-hidden border border-stone-800/80"
          style={{ background: 'rgba(28, 22, 16, 0.92)' }}>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8">
            <div className="flex items-center gap-2.5">
              <span className="flex gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
              </span>
              <span className="text-[11px] font-mono text-stone-400 ml-1">rsync output</span>
              {running && <RefreshCw className="w-2.5 h-2.5 animate-spin text-amber-400/70" />}
            </div>
            {lines.length > 0 && (
              <button className="text-[11px] text-stone-500 hover:text-stone-300"
                onClick={() => setLines([])}>Clear</button>
            )}
          </div>
          <div className="h-72 overflow-y-auto px-4 py-3 font-mono text-[11px] leading-relaxed space-y-px">
            {lines.length === 0 ? (
              <div className="text-stone-600">
                <p>$ # rsync terminal</p>
                <p>$ # Select servers and paths, then "Migrate Files"</p>
              </div>
            ) : lines.map(line => (
              <div key={line.id} className={cn('break-all', LINE_CLR[line.type] || 'text-stone-300')}>
                {line.content}
              </div>
            ))}
            {running && <div className="text-amber-500/70 animate-pulse">▌</div>}
            <div ref={endRef} />
          </div>
        </div>
      )}
    </div>
  )
}
